<template>
  <Teleport to="body">
    <div v-if="props.isOpen" class="rate-limit-backdrop" @click.self="closeModal">
      <div class="rate-limit-modal">
        <div class="rate-limit-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line
              x1="12"
              y1="9"
              x2="12"
              y2="13"
            />
            <line
              x1="12"
              y1="17"
              x2="12.01"
              y2="17"
            />
          </svg>
        </div>

        <h2 class="rate-limit-title">
          Trop de requêtes
        </h2>

        <p class="rate-limit-message">
          {{ message }}
        </p>

        <div class="rate-limit-timer">
          <div class="timer-circle">
            <svg viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                class="timer-bg"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                class="timer-progress"
                :style="{ strokeDashoffset: progressOffset }"
              />
            </svg>
            <span class="timer-text">{{ timeRemaining }}s</span>
          </div>
          <p class="timer-label">
            Réessayez dans
          </p>
        </div>

        <div class="rate-limit-buttons">
          <button class="rate-limit-button rate-limit-button-primary" @click="refreshPage">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
            Rafraîchir la page
          </button>
          <button class="rate-limit-button rate-limit-button-secondary" @click="closeModal">
            Fermer
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from '@vue/runtime-core';

interface Props {
  isOpen: boolean;
  message?: string;
  duration?: number; // en secondes
}

const props = withDefaults(defineProps<Props>(), {
  message: "Vous avez effectué trop de requêtes. Veuillez patienter quelques instants avant de réessayer.",
  duration: 60
});

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'timeout'): void;
}>();

const timeRemaining = ref(props.duration);
let intervalId: number | null = null;

const progressOffset = computed(() => {
  const circumference = 2 * Math.PI * 45;
  const progress = timeRemaining.value / props.duration;
  return circumference * (1 - progress);
});

const closeModal = () => {
  emit('close');
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

const refreshPage = () => {
  window.location.reload();
};

watch(() => props.isOpen, (newValue: boolean) => {
  console.log('[MODAL] isOpen changed to:', newValue);
  if (newValue) {
    console.log('[MODAL] Opening modal with message:', props.message, 'duration:', props.duration);
    timeRemaining.value = props.duration;

    if (intervalId) {
      clearInterval(intervalId);
    }

    intervalId = window.setInterval(() => {
      timeRemaining.value--;

      if (timeRemaining.value <= 0) {
        clearInterval(intervalId!);
        emit('timeout');
        closeModal();
      }
    }, 1000);
  } else {
    console.log('[MODAL] Modal closed');
  }
}, { immediate: true });

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});
</script>

<style scoped>
.rate-limit-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.rate-limit-modal {
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
  animation: slideUp 0.4s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.rate-limit-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  color: #f59e0b;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

.rate-limit-icon svg {
  width: 100%;
  height: 100%;
}

.rate-limit-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1rem;
}

.rate-limit-message {
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 2rem;
}

.rate-limit-timer {
  margin: 2rem 0;
}

.timer-circle {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 1rem;
}

.timer-circle svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.timer-bg {
  fill: none;
  stroke: #e5e7eb;
  stroke-width: 8;
}

.timer-progress {
  fill: none;
  stroke: #f59e0b;
  stroke-width: 8;
  stroke-linecap: round;
  stroke-dasharray: 283; /* 2 * PI * 45 */
  transition: stroke-dashoffset 1s linear;
}

.timer-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 2rem;
  font-weight: 700;
  color: #f59e0b;
}

.timer-label {
  font-size: 0.875rem;
  color: #9ca3af;
  margin: 0;
}

.rate-limit-buttons {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
}

.rate-limit-button {
  flex: 1;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.rate-limit-button-primary {
  background: #b70018;
  color: white;
}

.rate-limit-button-primary:hover {
  background: #950014;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(183, 0, 24, 0.3);
}

.rate-limit-button-secondary {
  background: #e5e7eb;
  color: #374151;
}

.rate-limit-button-secondary:hover {
  background: #d1d5db;
  transform: translateY(-1px);
}

.rate-limit-button:active {
  transform: translateY(0);
}

@media (max-width: 640px) {
  .rate-limit-modal {
    padding: 2rem;
  }

  .rate-limit-title {
    font-size: 1.5rem;
  }

  .rate-limit-icon {
    width: 60px;
    height: 60px;
  }

  .timer-circle {
    width: 100px;
    height: 100px;
  }

  .timer-text {
    font-size: 1.75rem;
  }
}
</style>
